/**
 * 2026-09 · Zähler-Modul.
 *
 * Zentrale Übersicht aller Zähler mit:
 *   • Suchfeld: sucht per Zählernummer (auch Teilstring)
 *   • Filter: Objekt, Zählertyp
 *   • Liste (Karten/Tabelle) mit letzter Ablesung
 *   • Detail-Dialog: alle Ablesungen + Schnellablesung + zum Objekt springen
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import SearchableCombobox from "@/components/SearchableCombobox";
import { calibrationInfo } from "@/components/meters/MeterExtras";
import { DatePicker } from "@/components/ui/date-picker";
import { formatDate } from "@/lib/constants";
import { MagnifyingGlass, Lightning, Camera, Plus, FloppyDisk, ArrowRight, X } from "@phosphor-icons/react";

const METER_TYPES = [
  { value: "electricity",         label: "Strom",                unit: "kWh" },
  { value: "common_electricity",  label: "Allgemeinstrom",       unit: "kWh" },
  { value: "gas",                 label: "Gas",                  unit: "m³" },
  { value: "cold_water",          label: "Kaltwasser",           unit: "m³" },
  { value: "hot_water",           label: "Warmwasser",           unit: "m³" },
  { value: "heat_quantity",       label: "Wärmemenge",           unit: "MWh" },
  { value: "district_heating",    label: "Fernwärme",            unit: "MWh" },
  { value: "heat_cost_allocator", label: "Heizkostenverteiler",  unit: "Einh." },
  { value: "other",               label: "Sonstiger",            unit: "" },
];

function typeLabel(t) {
  return METER_TYPES.find((x) => x.value === t)?.label || t || "—";
}

export default function Zaehler() {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [propertyFilter, setPropertyFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [properties, setProperties] = useState([]);
  const [openMeter, setOpenMeter] = useState(null); // {meter_id, property_id, ...}
  const [detail, setDetail] = useState(null); // { meter, readings, property, unit }
  const [readingValue, setReadingValue] = useState("");
  const [readingDate, setReadingDate] = useState(new Date().toISOString().slice(0, 10));
  const [readingNotes, setReadingNotes] = useState("");
  const [saving, setSaving] = useState(false);

  // Objekte für Filter
  useEffect(() => {
    api.get("/properties", { params: { include_deleted: false } })
      .then((r) => setProperties(r.data || []))
      .catch(() => setProperties([]));
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      if (query.trim().length >= 2) {
        const r = await api.get("/meters/search", {
          params: { q: query.trim(), property_id: propertyFilter || undefined, limit: 200 },
        });
        let list = r.data.items || [];
        if (typeFilter) list = list.filter((m) => m.meter_type === typeFilter);
        setItems(list);
      } else {
        const r = await api.get("/meters/all", {
          params: {
            property_id: propertyFilter || undefined,
            meter_type: typeFilter || undefined,
            limit: 500,
          },
        });
        setItems(r.data.items || []);
      }
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [query, propertyFilter, typeFilter]);

  const openDetail = async (hit) => {
    setOpenMeter(hit);
    setDetail(null);
    setReadingValue("");
    setReadingNotes("");
    setReadingDate(new Date().toISOString().slice(0, 10));
    try {
      const r = await api.get(`/meters/${hit.meter_id}`);
      setDetail(r.data);
    } catch (e) {
      toast.error("Zähler-Details konnten nicht geladen werden");
    }
  };

  const saveReading = async (force = false) => {
    if (!openMeter || !readingValue) return;
    setSaving(true);
    try {
      await api.post(`/meters/${openMeter.meter_id}/reading`, {
        property_id: openMeter.property_id,
        value: Number(String(readingValue).replace(",", ".")),
        reading_date: readingDate,
        notes: readingNotes || null,
        source: "manual",
        force,
      });
      toast.success("Ablesung gespeichert");
      // Reload detail + list
      const r = await api.get(`/meters/${openMeter.meter_id}`);
      setDetail(r.data);
      setReadingValue("");
      setReadingNotes("");
      load();
    } catch (e) {
      const status = e?.response?.status;
      const d = e?.response?.data?.detail;
      if (status === 409 && d && typeof d === "object") {
        const ok = window.confirm(`${d.detail}\n\nTrotzdem speichern?`);
        if (ok) return saveReading(true);
      } else {
        toast.error(typeof d === "string" ? d : "Speichern fehlgeschlagen");
      }
    } finally {
      setSaving(false);
    }
  };

  const propertyOptions = useMemo(
    () => properties.slice().sort((a, b) => (a.property_number || "").localeCompare(b.property_number || "")).map((p) => ({
      id: p.id,
      label: `${p.property_number || "—"} · ${p.name}`,
      sublabel: [p.address?.street, p.address?.city].filter(Boolean).join(", "),
      keywords: [p.property_number, p.name, p.address?.street, p.address?.city].filter(Boolean).join(" "),
    })),
    [properties],
  );

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto space-y-4" data-testid="page-zaehler">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold text-slate-900">Zähler</h1>
          <p className="text-sm text-slate-500 mt-1">
            Zähler per Zählernummer suchen, Ablesungen erfassen und die Historie einsehen.
          </p>
        </div>
        <Button
          variant="outline"
          className="gap-1.5"
          onClick={() => navigate("/zaehler/erfassen")}
          data-testid="zaehler-scanner-btn"
        >
          <Camera size={16} /> Scanner öffnen
        </Button>
      </div>

      {/* Search + Filter Row */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 md:p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-5">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 block">
              Zählernummer suchen
            </Label>
            <div className="relative">
              <MagnifyingGlass
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <Input
                autoFocus
                placeholder="z. B. 15005272, 1APA01..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 h-10 text-base"
                data-testid="zaehler-search-input"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                  data-testid="zaehler-search-clear"
                >
                  <X size={16} />
                </button>
              )}
            </div>
          </div>
          <div className="md:col-span-4">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 block">
              Objekt
            </Label>
            <SearchableCombobox
              value={propertyFilter}
              onChange={setPropertyFilter}
              options={propertyOptions}
              placeholder="Alle Objekte"
              allowClear
              testId="zaehler-property-filter"
            />
          </div>
          <div className="md:col-span-3">
            <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-1 block">
              Zählertyp
            </Label>
            <SearchableCombobox
              value={typeFilter}
              onChange={setTypeFilter}
              options={METER_TYPES.map((t) => ({ id: t.value, label: t.label, sublabel: t.unit || undefined, keywords: t.label }))}
              placeholder="Alle Typen"
              allowClear
              testId="zaehler-type-filter"
            />
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="text-xs text-slate-500 px-1" data-testid="zaehler-count">
        {loading ? "Lade…" : `${items.length} Zähler gefunden`}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {items.map((m) => (
          <button
            key={m.meter_id}
            type="button"
            onClick={() => openDetail(m)}
            className="text-left bg-white border border-slate-200 rounded-lg p-4 hover:border-teal-400 hover:shadow-md transition-all"
            data-testid={`zaehler-card-${m.meter_id}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-mono text-sm font-semibold text-slate-900 truncate">
                  {m.meter_number || "(ohne Nr.)"}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate">
                  {typeLabel(m.meter_type)}
                  {m.location ? ` · ${m.location}` : ""}
                </div>
                {m.active === false && <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 text-[10px] text-slate-600" data-testid={`zaehler-removed-${m.meter_id}`}>ausgebaut</span>}
                {m.planned_change && <span className="mt-1 ml-1 inline-block rounded bg-sky-50 px-1.5 text-[10px] text-sky-800" data-testid={`zaehler-planned-${m.meter_id}`}>Wechsel {m.planned_change.change_date?.split("-").reverse().join(".")}</span>}
                {(() => { const c = calibrationInfo(m); return c ? <div className={`text-[11px] mt-0.5 ${c.expired ? "text-rose-700 font-semibold" : "text-slate-500"}`} data-testid={`zaehler-calibration-${m.meter_id}`}>Eichung {c.label}{c.until ? ` · bis Ende ${c.until}` : ""}</div> : null; })()}
              </div>
              <ArrowRight size={16} className="text-slate-300 shrink-0 mt-1" />
            </div>
            {m.unit_label ? (
              <div className="mt-3 text-sm font-semibold text-teal-800 truncate" data-testid={`zaehler-unit-${m.meter_id}`}>
                Einheit {m.unit_label}
                {m.unit_tenant && <span className="block text-xs font-normal text-slate-600 truncate">Mieter: {m.unit_tenant}</span>}
              </div>
            ) : <div className="mt-3 text-xs text-slate-400">Objekt-Zähler (keiner Einheit zugeordnet)</div>}
            <div className="mt-1 text-xs text-slate-600 truncate">
              <span className="text-slate-400">Objekt:</span> {m.property_number || "—"} · {m.property_name || "—"}
            </div>
            <div className="mt-2 pt-2 border-t border-slate-100 flex items-center justify-between text-xs">
              <span className="text-slate-500">Letzte Ablesung</span>
              <span className="font-mono text-slate-900">
                {m.last_reading
                  ? `${m.last_reading.value} ${m.measurement_unit || ""} · ${formatDate(m.last_reading.reading_date)}`
                  : "—"}
              </span>
            </div>
          </button>
        ))}
        {!loading && items.length === 0 && (
          <div className="md:col-span-2 xl:col-span-3 py-12 text-center text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg">
            Keine Zähler gefunden. Passe die Suche/Filter an oder lege am Objekt einen neuen Zähler an.
          </div>
        )}
      </div>

      {/* Detail Dialog */}
      <Dialog open={!!openMeter} onOpenChange={(v) => !v && setOpenMeter(null)}>
        <DialogContent className="max-w-2xl" data-testid="zaehler-detail-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lightning size={18} className="text-teal-600" />
              <span className="font-mono">{openMeter?.meter_number}</span>
              <span className="text-sm text-slate-400 font-normal">· {typeLabel(openMeter?.meter_type)}</span>
            </DialogTitle>
            <DialogDescription>
              {openMeter?.property_number} · {openMeter?.property_name}
              {openMeter?.unit_label ? ` · Einheit ${openMeter.unit_label}` : ""}
            </DialogDescription>
          </DialogHeader>

          {!detail ? (
            <div className="py-8 text-center text-sm text-slate-500">Lade…</div>
          ) : (
            <div className="space-y-4">
              {/* Meta */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <div className="text-xs text-slate-500">Standort</div>
                  <div className="text-slate-900">{detail.meter.location || "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Einheit</div>
                  <div className="text-slate-900">
                    {detail.meter.measurement_unit || METER_TYPES.find((t) => t.value === detail.meter.meter_type)?.unit || "—"}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Einbau</div>
                  <div className="text-slate-900">{detail.meter.installed_date ? formatDate(detail.meter.installed_date) : "—"}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Notiz</div>
                  <div className="text-slate-900 truncate">{detail.meter.notes || "—"}</div>
                </div>
              </div>

              {/* New Reading Form */}
              {detail.meter.active === false ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600" data-testid="zaehler-removed-hint">
                  Dieser Zähler wurde am {formatDate(detail.meter.removed_at)} ausgebaut – keine neuen Ablesungen möglich.
                </div>
              ) : (
              <div className="border border-teal-200 bg-teal-50/40 rounded-lg p-3">
                <div className="text-xs font-semibold uppercase tracking-wider text-teal-800 mb-2">
                  Neue Ablesung erfassen
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-600">Wert</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={readingValue}
                      onChange={(e) => setReadingValue(e.target.value)}
                      placeholder="Zählerstand"
                      className="h-9 text-base font-mono"
                      data-testid="zaehler-reading-value"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-600">Datum</Label>
                    <DatePicker value={readingDate} onChange={setReadingDate} className="h-9" data-testid="zaehler-reading-date" />
                  </div>
                </div>
                <div className="mt-2">
                  <Textarea
                    rows={2}
                    value={readingNotes}
                    onChange={(e) => setReadingNotes(e.target.value)}
                    placeholder="Notiz (optional)"
                    className="text-sm"
                    data-testid="zaehler-reading-notes"
                  />
                </div>
                <div className="mt-2 flex justify-end">
                  <Button
                    onClick={() => saveReading(false)}
                    disabled={!readingValue || saving}
                    className="gap-1.5 bg-teal-600 hover:bg-teal-700"
                    data-testid="zaehler-save-reading"
                  >
                    <FloppyDisk size={14} /> {saving ? "Speichert…" : "Speichern"}
                  </Button>
                </div>
              </div>
              )}

              {/* Readings History */}
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 mb-2">
                  Historie ({detail.readings.length})
                </div>
                {detail.readings.length === 0 ? (
                  <div className="text-sm text-slate-500 py-3 text-center border border-dashed rounded">
                    Noch keine Ablesungen erfasst.
                  </div>
                ) : (
                  <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600">Datum</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold text-slate-600">Wert</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold text-slate-600 hidden md:table-cell">Notiz</th>
                        </tr>
                      </thead>
                      <tbody>
                        {detail.readings.slice().reverse().map((r) => (
                          <tr key={r.id} className="border-t border-slate-100">
                            <td className="px-3 py-1.5 text-slate-800">{formatDate(r.reading_date)}</td>
                            <td className="px-3 py-1.5 text-right font-mono text-slate-900">
                              {r.value} <span className="text-xs text-slate-400">{detail.meter.measurement_unit || ""}</span>
                            </td>
                            <td className="px-3 py-1.5 text-slate-500 text-xs hidden md:table-cell truncate max-w-xs">{r.notes || ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" asChild data-testid="zaehler-open-property">
              <Link to={`/objekte/${openMeter?.property_id}?tab=meters`}>Zum Objekt</Link>
            </Button>
            <Button variant="ghost" onClick={() => setOpenMeter(null)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
